/**
 * ColumnSelector Component
 *
 * Allows users to show/hide columns and reorder them
 * - Checkbox list for column visibility
 * - Drag & drop to reorder columns
 * - "Reset to default" button
 * - Save preferences to localStorage
 */

import React, { useState, useEffect } from 'react';
import { Popover, Button, Checkbox, Space, Divider, Typography } from 'antd';
import {
  SettingOutlined,
  DragOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { UDCColumnDefinition } from '@/api/types/udc.types';
import './ColumnSelector.css';

const { Text } = Typography;

interface ColumnSelectorProps {
  columns: UDCColumnDefinition[];
  collection: string;
  onColumnsChange: (visibleColumns: string[], columnOrder: string[]) => void;
}

interface ColumnState {
  name: string;
  visible: boolean;
  order: number;
}

export const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  collection,
  onColumnsChange,
}) => {
  const [open, setOpen] = useState(false);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);

  // Initialize column state from localStorage or defaults
  const [columnStates, setColumnStates] = useState<ColumnState[]>(() => {
    const storageKey = `${collection}_column_preferences`;
    const saved = localStorage.getItem(storageKey);

    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Merge saved preferences with current columns (in case columns changed)
        return columns.map((col, index) => {
          const savedCol = parsed.find((s: ColumnState) => s.name === col.name);
          return savedCol || { name: col.name, visible: true, order: index };
        });
      } catch {
        // Invalid JSON, use defaults
      }
    }

    // Default: all visible, original order
    return columns.map((col, index) => ({
      name: col.name,
      visible: true,
      order: index,
    }));
  });

  // Save to localStorage whenever state changes
  useEffect(() => {
    const storageKey = `${collection}_column_preferences`;
    localStorage.setItem(storageKey, JSON.stringify(columnStates));

    // Notify parent of changes
    const visibleColumns = columnStates
      .filter((c) => c.visible)
      .sort((a, b) => a.order - b.order)
      .map((c) => c.name);

    const columnOrder = columnStates
      .sort((a, b) => a.order - b.order)
      .map((c) => c.name);

    onColumnsChange(visibleColumns, columnOrder);
  }, [columnStates, collection, onColumnsChange]);

  // Toggle column visibility
  const toggleColumn = (columnName: string) => {
    setColumnStates((prev) =>
      prev.map((col) =>
        col.name === columnName ? { ...col, visible: !col.visible } : col
      )
    );
  };

  // Toggle all columns
  const toggleAll = (visible: boolean) => {
    setColumnStates((prev) => prev.map((col) => ({ ...col, visible })));
  };

  // Reset to defaults
  const resetToDefaults = () => {
    const defaults = columns.map((col, index) => ({
      name: col.name,
      visible: true,
      order: index,
    }));
    setColumnStates(defaults);
  };

  // Drag handlers
  const handleDragStart = (columnName: string) => {
    setDraggedItem(columnName);
  };

  const handleDragOver = (e: React.DragEvent, targetName: string) => {
    e.preventDefault();

    if (!draggedItem || draggedItem === targetName) return;

    // Reorder columns
    setColumnStates((prev) => {
      const draggedIndex = prev.findIndex((c) => c.name === draggedItem);
      const targetIndex = prev.findIndex((c) => c.name === targetName);

      if (draggedIndex === -1 || targetIndex === -1) return prev;

      const newStates = [...prev];
      const [draggedCol] = newStates.splice(draggedIndex, 1);
      newStates.splice(targetIndex, 0, draggedCol);

      // Update order values
      return newStates.map((col, index) => ({ ...col, order: index }));
    });
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  // Get column display name
  const getColumnDisplayName = (columnName: string): string => {
    const col = columns.find((c) => c.name === columnName);
    return col?.display_name || columnName;
  };

  // Sort column states by order
  const sortedColumnStates = [...columnStates].sort(
    (a, b) => a.order - b.order
  );

  // Count visible columns
  const visibleCount = columnStates.filter((c) => c.visible).length;

  const content = (
    <div className="column-selector-content">
      {/* Header with actions */}
      <div className="column-selector-header">
        <Space>
          <Button type="link" size="small" onClick={() => toggleAll(true)}>
            Show All
          </Button>
          <Button type="link" size="small" onClick={() => toggleAll(false)}>
            Hide All
          </Button>
          <Button
            type="link"
            size="small"
            icon={<ReloadOutlined />}
            onClick={resetToDefaults}
          >
            Reset
          </Button>
        </Space>
      </div>

      <Divider style={{ margin: '8px 0' }} />

      {/* Column list with drag & drop */}
      <div className="column-list">
        {sortedColumnStates.map((colState) => (
          <div
            key={colState.name}
            className={`column-item ${draggedItem === colState.name ? 'dragging' : ''}`}
            draggable
            onDragStart={() => handleDragStart(colState.name)}
            onDragOver={(e) => handleDragOver(e, colState.name)}
            onDragEnd={handleDragEnd}
          >
            <Space size="small" style={{ width: '100%' }}>
              <DragOutlined className="drag-handle" />
              <Checkbox
                checked={colState.visible}
                onChange={() => toggleColumn(colState.name)}
              >
                {getColumnDisplayName(colState.name)}
              </Checkbox>
            </Space>
          </div>
        ))}
      </div>

      <Divider style={{ margin: '8px 0' }} />

      {/* Footer info */}
      <div className="column-selector-footer">
        <Text type="secondary" style={{ fontSize: 12 }}>
          {visibleCount} of {columns.length} columns visible
        </Text>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={setOpen}
      overlayClassName="column-selector-popover"
    >
      <Button icon={<SettingOutlined />}>Columns</Button>
    </Popover>
  );
};
